/**
 * Programmatic comparison pages: /compare (index) and /compare/:slug (detail).
 * Data lives in src/data/comparisons.ts. Each detail page sets its own SEO meta +
 * FAQPage JSON-LD.
 */
import { BrandMark } from '../components/BrandMark';
import { Link, useParams, Navigate } from 'react-router-dom';
import { comparisons, getComparison } from '../data/comparisons';
import { useSeo } from '../hooks/useSeo';

const SITE = 'https://agent-mesh.org';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <span className="text-lg font-bold">Agent<span className="text-brand">Mesh</span></span>
          </Link>
          <Link
            to="/register"
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Start free
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-12">{children}</main>
      <footer className="border-t">
        <div className="mx-auto max-w-4xl px-5 py-8 text-sm text-slate-500">
          {/* /example and /blog also live in the prerendered footer in
              gen-content-pages.mjs — both were orphans. Keep the two in step. */}
          <Link to="/compare" className="hover:underline">
            All comparisons
          </Link>{' '}
          · <Link to="/example" className="hover:underline">See a real run</Link>{' '}
          {/* Plain <a>: /agent-library is prerendered HTML with no React route, so a
              <Link> would render NotFound. */}
          · <a href="/agent-library" className="hover:underline">Agents</a>{' '}
          · <Link to="/blog" className="hover:underline">Writing</Link>{' '}
          · <Link to="/" className="hover:underline">Home</Link>
        </div>
      </footer>
    </div>
  );
}

export function ComparisonsIndex() {
  useSeo({
    title: 'Compare AgentMesh',
    description:
      'How AgentMesh — no-code teams of AI agents that reason, use tools and automate — compares to popular automation tools.',
    canonical: `${SITE}/compare`,
  });
  return (
    <Shell>
      <h1 className="mb-2 text-3xl font-bold">Compare AgentMesh</h1>
      <p className="mb-2 max-w-2xl text-slate-600">
        How AgentMesh — no-code teams of AI agents that reason, use tools, and
        automate your operations — compares to popular automation tools.
      </p>
      <p className="mb-8 text-sm text-slate-500">
        Also:{' '}
        <Link to="/solutions" className="text-brand hover:underline">
          AI agents by industry
        </Link>{' '}
        ·{' '}
        <Link to="/help" className="text-brand hover:underline">
          Help center
        </Link>
      </p>
      <ul className="space-y-3">
        {comparisons.map((c) => (
          <li key={c.slug}>
            <Link
              to={`/compare/${c.slug}`}
              className="block rounded-lg border p-4 hover:border-slate-400"
            >
              <span className="text-lg font-semibold">
                AgentMesh vs {c.competitor}
              </span>
              <p className="mt-1 text-sm text-slate-600">{c.metaDescription}</p>
            </Link>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

/**
 * Shared tail for every /compare page — mirrors compareExtrasHtml() in
 * scripts/dev/gen-content-pages.mjs. The two MUST stay in step: that file
 * prerenders these pages for crawlers, and every divergence bug in this repo
 * came from adding to one copy and not the other.
 *
 * Contains no claim about any competitor. Every competitor claim lives in
 * data/comparisons.ts with a date and a source URL beside it.
 */
function CompareExtras() {
  return (
    <>
      <h2 className="mb-3 text-xl font-semibold">What a run costs here</h2>
      <p className="mb-3 text-slate-700">
        Runs are billed in tokens, so cost follows the size of the job rather than a per-task fee. A
        worked support-ticket example came to <strong>$0.0144</strong>; a real logged run that also escalated
        to a human <strong>$0.0204</strong>; one that failed on its first tool call{' '}
        <strong>$0.0024</strong>. The dearest agents shipped here reach about 11 cents. Source:{' '}
        <a href="/example" className="underline">three runs opened up step by step</a>, readable
        without an account.
      </p>
      <p className="mb-3 text-slate-700">
        On the free plan those runs draw on a daily allowance of{' '}
        <strong>100 runs or $0.50 of usage, whichever is reached first</strong>; on Pro at $29 a
        month, 1,000 runs or $2.00. Bring your own Anthropic or OpenAI key on either plan and neither
        ceiling applies, because you pay the model provider directly and we do not mark up tokens.
      </p>
      <p className="mb-10 text-slate-700">
        One figure worth knowing before you compare anything: in that escalating run,{' '}
        <strong>89% of the tokens were input rather than output</strong>. What you feed an agent —
        including everything a tool hands back — usually costs more than what it says.
      </p>

      <h2 className="mb-3 text-xl font-semibold">Where AgentMesh is the wrong choice</h2>
      <p className="mb-3 text-slate-700">
        <strong>I build this, so I am biased.</strong> The honest way to read a comparison published
        by a vendor is to look for the parts where it loses, so here they are.
      </p>
      <h3 className="mb-1 mt-4 font-medium">You need a specific connector, maintained by someone else</h3>
      <p className="mb-3 text-slate-700">
        There are <strong>ten tools</strong> here — HTTP, webhook, email, Slack, Discord, Google
        Chat, Telegram, Notion and two Google Calendar tools — plus any REST API you can reach.
        “Reachable with an HTTP call” is not the same as a maintained connector with someone else on
        the hook when the vendor changes their API, and a mature catalogue of hundreds is a real
        advantage.
      </p>
      <h3 className="mb-1 mt-4 font-medium">The same input must produce the same output</h3>
      <p className="mb-3 text-slate-700">
        Agents reason, which means they vary. Two identical tickets can produce differently worded
        replies. Structured JSON output fixes the shape but not the wording, so if byte-identical
        output matters, a deterministic tool is the right answer and an agent is not.
      </p>
      <h3 className="mb-1 mt-4 font-medium">Procurement needs a certificate</h3>
      <p className="mb-3 text-slate-700">
        This is <strong>GDPR-aware and encrypts secrets at rest, and it has no SOC 2 or HIPAA
        report.</strong> Those are certifications, not postures, and we do not hold them. If your
        buyer requires one, that is a hard stop today and no feature comparison changes it.
      </p>
      <p className="mb-10 text-slate-700">
        Also worth knowing: billing is per account rather than per seat, and the account owner
        manages the plan — so a team that needs seat-level billing and roles will find this thin.
      </p>

      <h2 className="mb-3 text-xl font-semibold">What actually separates them</h2>
      <p className="mb-10 text-slate-700">
        Not the feature grid. It is what happens on an input nobody anticipated: a deterministic
        automation does the same thing regardless, and an agent decides. That is the value and the
        risk in one sentence, and the risk is real — Source:{' '}
        <a href="/blog-assets/run-fff256c0.json" className="underline">a logged run of mine</a>{' '}
        finished <code>COMPLETED</code> after two lookups returned HTTP 403 with{' '}
        <code>"ok": true</code> in the tool result, and told the customer it had checked her orders.
        Read the step rows, not the status.
      </p>
    </>
  );
}

export function ComparePage() {
  const { slug = '' } = useParams();
  const c = getComparison(slug);

  useSeo({
    title: c ? c.title : 'Compare AgentMesh',
    description: c?.metaDescription,
    canonical: c ? `${SITE}/compare/${c.slug}` : `${SITE}/compare`,
    jsonLd: c
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: c.faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Compare', item: `${SITE}/compare` },
              { '@type': 'ListItem', position: 2, name: c.title, item: `${SITE}/compare/${c.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!c) return <Navigate to="/compare" replace />;

  return (
    <Shell>
      <p className="mb-2 text-sm font-medium text-slate-500">Comparison</p>
      <h1 className="mb-4 text-3xl font-bold">{c.title}</h1>
      <div className="mb-8 max-w-2xl rounded-lg border-l-4 border-slate-900 bg-slate-50 p-4">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Quick answer
        </p>
        <p className="text-slate-800">{c.intro}</p>
      </div>

      <div className="mb-10 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-3 pr-4 font-semibold">Feature</th>
              <th className="py-3 pr-4 font-semibold">AgentMesh</th>
              <th className="py-3 font-semibold">{c.competitor}</th>
            </tr>
          </thead>
          <tbody>
            {c.rows.map((r) => (
              <tr key={r.feature} className="border-b last:border-0 align-top">
                <td className="py-3 pr-4 font-medium">{r.feature}</td>
                <td className="py-3 pr-4 text-slate-700">{r.agentMesh}</td>
                <td className="py-3 text-slate-600">{r.competitor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Provenance. Must stay in step with the same line in
          scripts/dev/gen-content-pages.mjs — that file prerenders this page for
          crawlers, and if only one of the two carries the date, the version a
          crawler reads and the version a human reads disagree. */}
      <p className="mb-10 border-t border-[var(--line,#e5e7eb)] pt-3 text-sm text-muted-foreground">
        Source:{' '}
        <a href={c.sourceUrl} rel="nofollow noopener" target="_blank" className="underline">
          {c.competitor}'s own published pricing
        </a>
        , last checked <strong>{c.lastVerified}</strong>. Prices and features change — check theirs
        before deciding, and tell us if this table is out of date.
      </p>

      <CompareExtras />

      <h2 className="mb-4 text-xl font-semibold">Frequently asked questions</h2>
      <div className="mb-10 space-y-4">
        {c.faqs.map((f) => (
          <div key={f.q}>
            {/* A question is a heading: the FAQPage schema says Question while this
                said paragraph. Changed with faqHtml in gen-content-pages.mjs so
                the prerendered twin matches. */}
            <h3 className="font-medium">{f.q}</h3>
            <p className="mt-1 text-slate-600">{f.a}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-slate-50 p-6 text-center">
        <p className="mb-3 text-lg font-semibold">
          Try AgentMesh free — build a working agent in minutes
        </p>
        <Link
          to="/register"
          className="inline-block rounded-md bg-slate-900 px-5 py-2.5 font-medium text-white hover:bg-slate-700"
        >
          Start free — no card required
        </Link>
      </div>

      <div className="mt-10 border-t pt-6">
        <h2 className="mb-3 text-lg font-semibold">Keep exploring</h2>
        <ul className="grid gap-2 text-sm sm:grid-cols-2">
          {comparisons
            .filter((x) => x.slug !== c.slug)
            .slice(0, 4)
            .map((x) => (
              <li key={x.slug}>
                <Link
                  to={`/compare/${x.slug}`}
                  className="text-brand hover:underline"
                >
                  AgentMesh vs {x.competitor}
                </Link>
              </li>
            ))}
          <li>
            <Link to="/solutions" className="text-brand hover:underline">
              AI agents by industry
            </Link>
          </li>
          <li>
            <Link
              to="/help/ai-agents-vs-automation-tools"
              className="text-brand hover:underline"
            >
              AI agents vs automation tools
            </Link>
          </li>
        </ul>
      </div>
    </Shell>
  );
}
